/**
 * Extracts and calculates the correct package totals to avoid double-counting transport.
 * 
 * The backend grandTotal (`breakdownTotal`) already includes transport.
 * If transport is toggled OFF, we subtract the transport amount from the backend total.
 *
 * @param {Object} pkg - The package object (from estimateData.packages)
 * @param {Object} estimateData - The root estimate data object
 * @returns {Object} Adjusted pricing information
 */
export const calculatePackageTotals = (pkg, estimateData) => {
  // Determine if transport is toggled on (defaults to true if undefined)
  const includeTransport = (pkg.includeTransport ?? estimateData?.includeTransport ?? estimateData?.input?.includeTransport ?? estimateData?.transport?.includeTransport) !== false;
  
  // Extract the raw transport amount provided by the backend
  const transportAmount = pkg.breakdown?.transport ?? estimateData?.transportTotal ?? estimateData?.transport?.total ?? 0;
  
  // Extract raw backend totals (which already INCLUDE transport if it was part of the original calculation)
  const rawMid = pkg.breakdownTotal || pkg.grandTotal?.mid || 0;
  const rawMin = pkg.grandTotal?.min || 0;
  const rawMax = pkg.grandTotal?.max || 0;
  
  // If transport is toggled OFF by the user, subtract it from the backend total
  const adjust = (amount) => {
    if (!amount) return 0;
    return includeTransport ? amount : Math.max(0, amount - transportAmount);
  };
  
  const grandTotalMid = adjust(rawMid);
  const ratio = rawMid > 0 ? (grandTotalMid / rawMid) : 1;

  const adjustPerPax = (amount) => {
    if (!amount) return 0;
    return includeTransport ? amount : Math.round(amount * ratio);
  };

  return {
    includeTransport,
    transportAmount: includeTransport ? transportAmount : 0, // What to display in the breakdown line item
    grandTotalMid,
    grandTotalMin: rawMin ? adjust(rawMin) : null,
    grandTotalMax: rawMax ? adjust(rawMax) : null,
    
    // Adjusted per-pax costs
    perPersonDoubleSharingMid: adjustPerPax(pkg.perPersonDoubleSharing?.mid),
    perPersonDoubleSharingMin: adjustPerPax(pkg.perPersonDoubleSharing?.min),
    perPersonDoubleSharingMax: adjustPerPax(pkg.perPersonDoubleSharing?.max),
    adultCostMid: adjustPerPax(pkg.perPaxTypeBreakdown?.A?.mid),
    aebCostMid: adjustPerPax(pkg.perPaxTypeBreakdown?.AEB?.mid),
    cwbCostMid: adjustPerPax(pkg.perPaxTypeBreakdown?.CwB?.mid),
    cnbCostMid: adjustPerPax(pkg.perPaxTypeBreakdown?.CnB?.mid)
  };
};
